import type { BiomeId } from '@/styles/theme'

export type StarCount = 0 | 1 | 2 | 3

export interface LevelBest {
  slides: number
  coins: number
  timeMs: number
  score: number
}

export interface GraphicsSettings {
  graphics: 'high' | 'medium' | 'low'
  reducedMotion: boolean
  colorblind: boolean
  haptics: boolean
  autoFullscreen: boolean
}

export interface TakeMeHomeSave {
  version: 3
  progress: {
    completedLevels: number[]
    levelStars: Record<number, StarCount>
    levelBest: Record<number, LevelBest>
    currentBiome: BiomeId
    unlockedBiomes: BiomeId[]
    endlessBest: { streak: number; score: number }
  }
  economy: {
    coins: number
    gems: number
    lives: number
    livesUpdatedAt: number
    energy: number
  }
  cosmetics: {
    equippedSkin: string
    ownedSkins: string[]
    equippedTrail: string
    ownedTrails: string[]
    ownedProps: string[]
    equippedProps: string[]
    equippedCharacter: string
    ownedCharacters: string[]
  }
  powerups: Record<string, number>
  meta: {
    totalStars: number
    graceScore: number
    totalScore: number
    achievements: string[]
    daily: { lastClaimIso: string; streakDay: number }
    home: { lastReturnedHomeAt: number }
    removeAds: boolean
    totalSpent: { coins: number; gems: number }
    vipTier: number
    tutorialSeen: boolean
    settings: GraphicsSettings
  }
  updatedAt: number
}

export const MAX_LIVES = 5

export const DEFAULT_SAVE: TakeMeHomeSave = {
  version: 3,
  progress: {
    completedLevels: [],
    levelStars: {},
    levelBest: {},
    currentBiome: 'forest',
    unlockedBiomes: ['forest'],
    endlessBest: { streak: 0, score: 0 },
  },
  economy: {
    coins: 0,
    gems: 0,
    lives: MAX_LIVES,
    livesUpdatedAt: Date.now(),
    energy: 100,
  },
  cosmetics: {
    equippedSkin: 'ember',
    ownedSkins: ['ember'],
    equippedTrail: 'none',
    ownedTrails: ['none'],
    ownedProps: [],
    equippedProps: [],
    equippedCharacter: 'default_hero',
    ownedCharacters: ['default_hero'],
  },
  powerups: {},
  meta: {
    totalStars: 0,
    graceScore: 0,
    totalScore: 0,
    achievements: [],
    daily: { lastClaimIso: '', streakDay: 0 },
    home: { lastReturnedHomeAt: Date.now() },
    removeAds: false,
    totalSpent: { coins: 0, gems: 0 },
    vipTier: 0,
    tutorialSeen: false,
    settings: {
      graphics: 'high',
      reducedMotion: false,
      colorblind: false,
      haptics: true,
      autoFullscreen: true,
    },
  },
  updatedAt: Date.now(),
}

type AnySave = {
  version?: number
  progress?: Partial<TakeMeHomeSave['progress']>
  economy?: Partial<TakeMeHomeSave['economy']>
  cosmetics?: Partial<TakeMeHomeSave['cosmetics']>
  powerups?: Record<string, number>
  meta?: Partial<TakeMeHomeSave['meta']>
  updatedAt?: number
}

function asArray<T>(value: unknown, fallback: T[]): T[] {
  return Array.isArray(value) ? (value as T[]) : fallback
}

function asRecord<T>(value: unknown, fallback: Record<string, T>): Record<string, T> {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, T>) : fallback
}

function isCompleteSave(raw: AnySave): raw is TakeMeHomeSave {
  const p = raw.progress
  const e = raw.economy
  const c = raw.cosmetics
  const m = raw.meta
  return (
    raw.version === 3 &&
    !!p &&
    Array.isArray(p.completedLevels) &&
    !!p.levelStars &&
    typeof p.levelStars === 'object' &&
    !!p.levelBest &&
    typeof p.levelBest === 'object' &&
    typeof p.currentBiome === 'string' &&
    Array.isArray(p.unlockedBiomes) &&
    typeof p.endlessBest?.streak === 'number' &&
    typeof p.endlessBest?.score === 'number' &&
    !!e &&
    typeof e.coins === 'number' &&
    typeof e.gems === 'number' &&
    typeof e.lives === 'number' &&
    typeof e.livesUpdatedAt === 'number' &&
    typeof e.energy === 'number' &&
    !!c &&
    typeof c.equippedSkin === 'string' &&
    Array.isArray(c.ownedSkins) &&
    typeof c.equippedTrail === 'string' &&
    Array.isArray(c.ownedTrails) &&
    Array.isArray(c.ownedProps) &&
    Array.isArray(c.equippedProps) &&
    typeof c.equippedCharacter === 'string' &&
    Array.isArray(c.ownedCharacters) &&
    c.ownedCharacters.includes('default_hero') &&
    !!raw.powerups &&
    typeof raw.powerups === 'object' &&
    !!m &&
    typeof m.totalStars === 'number' &&
    typeof m.graceScore === 'number' &&
    typeof m.totalScore === 'number' &&
    Array.isArray(m.achievements) &&
    typeof m.daily?.lastClaimIso === 'string' &&
    typeof m.daily?.streakDay === 'number' &&
    typeof m.home?.lastReturnedHomeAt === 'number' &&
    typeof m.removeAds === 'boolean' &&
    typeof m.totalSpent?.coins === 'number' &&
    typeof m.totalSpent?.gems === 'number' &&
    typeof m.vipTier === 'number' &&
    typeof m.tutorialSeen === 'boolean' &&
    typeof m.settings?.graphics === 'string' &&
    typeof m.settings?.reducedMotion === 'boolean' &&
    typeof m.settings?.colorblind === 'boolean' &&
    typeof m.settings?.haptics === 'boolean' &&
    typeof m.settings?.autoFullscreen === 'boolean' &&
    typeof raw.updatedAt === 'number'
  )
}

export function migrateSave(raw: unknown): TakeMeHomeSave {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_SAVE }
  const old = raw as AnySave
  if (isCompleteSave(old)) return old
  const d = DEFAULT_SAVE
  return {
    version: 3,
    progress: {
      completedLevels: asArray(old.progress?.completedLevels, d.progress.completedLevels),
      levelStars: asRecord(old.progress?.levelStars, d.progress.levelStars),
      levelBest: asRecord(old.progress?.levelBest, d.progress.levelBest),
      currentBiome: old.progress?.currentBiome ?? d.progress.currentBiome,
      unlockedBiomes: asArray(old.progress?.unlockedBiomes, d.progress.unlockedBiomes),
      endlessBest: {
        streak: old.progress?.endlessBest?.streak ?? 0,
        score: old.progress?.endlessBest?.score ?? 0,
      },
    },
    economy: {
      coins: old.economy?.coins ?? d.economy.coins,
      gems: old.economy?.gems ?? d.economy.gems,
      lives: old.economy?.lives ?? MAX_LIVES,
      livesUpdatedAt: old.economy?.livesUpdatedAt ?? Date.now(),
      energy: old.economy?.energy ?? d.economy.energy,
    },
    cosmetics: {
      equippedSkin: old.cosmetics?.equippedSkin ?? d.cosmetics.equippedSkin,
      ownedSkins: asArray(old.cosmetics?.ownedSkins, d.cosmetics.ownedSkins),
      equippedTrail: old.cosmetics?.equippedTrail ?? d.cosmetics.equippedTrail,
      ownedTrails: asArray(old.cosmetics?.ownedTrails, d.cosmetics.ownedTrails),
      ownedProps: asArray(old.cosmetics?.ownedProps, []),
      equippedProps: asArray(old.cosmetics?.equippedProps, []),
      equippedCharacter: old.cosmetics?.equippedCharacter ?? 'default_hero',
      ownedCharacters:
        Array.isArray(old.cosmetics?.ownedCharacters) && old.cosmetics.ownedCharacters.length > 0
          ? old.cosmetics.ownedCharacters.includes('default_hero')
            ? old.cosmetics.ownedCharacters
            : ['default_hero', ...old.cosmetics.ownedCharacters]
          : ['default_hero'],
    },
    powerups: asRecord(old.powerups, {}),
    meta: {
      totalStars: old.meta?.totalStars ?? 0,
      graceScore: old.meta?.graceScore ?? 0,
      totalScore: old.meta?.totalScore ?? 0,
      achievements: asArray(old.meta?.achievements, []),
      daily: {
        lastClaimIso: old.meta?.daily?.lastClaimIso ?? '',
        streakDay: old.meta?.daily?.streakDay ?? 0,
      },
      home: {
        lastReturnedHomeAt:
          typeof old.meta?.home?.lastReturnedHomeAt === 'number'
            ? old.meta.home.lastReturnedHomeAt
            : (old.updatedAt ?? Date.now()),
      },
      removeAds: old.meta?.removeAds ?? false,
      totalSpent: {
        coins: old.meta?.totalSpent?.coins ?? 0,
        gems: old.meta?.totalSpent?.gems ?? 0,
      },
      vipTier: old.meta?.vipTier ?? 0,
      tutorialSeen: old.meta?.tutorialSeen ?? false,
      settings: {
        graphics: old.meta?.settings?.graphics ?? 'high',
        reducedMotion: old.meta?.settings?.reducedMotion ?? false,
        colorblind: old.meta?.settings?.colorblind ?? false,
        haptics: old.meta?.settings?.haptics ?? true,
        autoFullscreen: old.meta?.settings?.autoFullscreen ?? true,
      },
    },
    updatedAt: old.updatedAt ?? Date.now(),
  }
}
